import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelpers } from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { useAuthStore } from '@/stores/auth';
import { formatDate } from '@/lib/utils';
import type { UserRole, CreateUserData } from '@/types';
import { UserCell } from '@/components/ui/user-cell';
import { RoleBadge } from '@/components/ui/role-badge';
import { StatusBadge } from '@/components/ui/status-badge';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingPage } from '@/components/ui/loading-spinner';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DeleteUserDialog } from '@/components/users/DeleteUserDialog';
import {
  Plus,
  Search,
  Users as UsersIcon,
  MoreVertical,
  Loader2,
  X,
  Shield,
  ShieldOff,
  LogIn,
  Trash2,
  UserCog,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Types for menu state
// ---------------------------------------------------------------------------
interface MenuState {
  userId: string;
  userName: string;
  userRole: string;
  isActive: boolean;
  top: number;
  left: number;
}

// ---------------------------------------------------------------------------
// Main Users page
// ---------------------------------------------------------------------------
export default function Users() {
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [roleSubmenuOpen, setRoleSubmenuOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'delete' | 'suspend' | 'unsuspend' | 'role';
    userId: string;
    userName: string;
    newRole?: UserRole;
  } | null>(null);
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', search],
    queryFn: () => apiHelpers.getUsers({ limit: 50 }),
  });

  const users = data?.data?.data || [];

  const canManageUsers = user?.role === 'SUPER_ADMIN';

  // --- Mutations ---

  const deleteMutation = useMutation({
    mutationFn: ({
      userId,
      transferOwnership,
    }: {
      userId: string;
      transferOwnership?: Record<string, string>;
    }) => apiHelpers.deleteUser(userId, transferOwnership),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // The cascade reports what it could NOT finish (a Cloudflare or gateway
      // call that failed). Swallowing those would leave an operator believing
      // a teardown completed when infra is still standing.
      const warnings = (res.data.data as { warnings?: string[] } | undefined)?.warnings;
      if (warnings?.length) {
        toast.warning(`User deleted with ${warnings.length} warning(s)`, {
          description: warnings[0],
        });
      } else {
        toast.success('User deleted');
      }
      setConfirmAction(null);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to delete user')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: any }) =>
      apiHelpers.updateUser(userId, data),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      if (vars.data.isActive === false) toast.success('User suspended');
      else if (vars.data.isActive === true) toast.success('User unsuspended');
      else if (vars.data.role) toast.success('Role updated');
      setConfirmAction(null);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update user')),
  });

  const impersonateMutation = useMutation({
    mutationFn: (userId: string) => apiHelpers.impersonateUser(userId),
    onSuccess: (res) => {
      const payload = res.data.data;
      if (!payload) return;
      const { ghostToken, user: targetUser, impersonatedBy } = payload;

      // Store ghost login data in localStorage temporarily for the new tab to pick up.
      // The new tab reads it, moves to sessionStorage, and deletes from localStorage.
      // This never touches the admin's auth-storage key.
      localStorage.setItem(
        'ghost_pending',
        JSON.stringify({ ghostToken, user: targetUser, impersonatedBy })
      );

      // Open the ghost login page in a new tab
      window.open('/auth/ghost-login', '_blank');

      // Clean up pending data after a short delay (in case new tab already read it)
      setTimeout(() => localStorage.removeItem('ghost_pending'), 5000);

      const name = `${targetUser.firstName} ${targetUser.lastName}`;
      toast.success(`Opened ghost session as ${name} (1 hour)`);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to impersonate user')),
  });

  const closeMenu = () => {
    setMenu(null);
    setRoleSubmenuOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="text-muted-foreground">Manage user accounts and permissions</p>
        </div>
        {canManageUsers && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-4 py-2 font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add User
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-background focus:ring-primary w-full rounded-lg border py-2 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2"
        />
      </div>

      {/* Users Table */}
      {isLoading ? (
        <LoadingPage />
      ) : error ? (
        <EmptyState
          icon={UsersIcon}
          title="Failed to load users"
          description="Please try again later."
        />
      ) : users.length === 0 ? (
        <EmptyState
          icon={UsersIcon}
          title="No users found"
          description={search ? 'Try a different search term' : 'Add your first team member'}
        />
      ) : (
        <div className="bg-card overflow-hidden rounded-xl border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-muted-foreground px-6 py-3 text-left text-sm font-medium">
                    User
                  </th>
                  <th className="text-muted-foreground px-6 py-3 text-left text-sm font-medium">
                    Role
                  </th>
                  <th className="text-muted-foreground px-6 py-3 text-left text-sm font-medium">
                    Status
                  </th>
                  <th className="text-muted-foreground px-6 py-3 text-left text-sm font-medium">
                    Projects
                  </th>
                  <th className="text-muted-foreground px-6 py-3 text-left text-sm font-medium">
                    Created
                  </th>
                  <th className="text-muted-foreground px-6 py-3 text-right text-sm font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map((u: any) => {
                  const isSuspended = u.isActive === false;
                  return (
                    <tr
                      key={u.id}
                      className={`hover:bg-muted/30 ${isSuspended ? 'opacity-60' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <UserCell
                          id={u.id}
                          firstName={u.firstName}
                          lastName={u.lastName}
                          email={u.email}
                          avatarClassName={
                            isSuspended ? 'bg-red-100 dark:bg-red-900/30' : undefined
                          }
                        />
                      </td>
                      <td className="px-6 py-4">
                        {/* Show user-level role first (this is what the row-
                            action dropdown actually mutates). Team role goes
                            beneath as a smaller secondary badge when present,
                            so admins can still see "owner of X team" etc. */}
                        <div className="flex flex-col gap-1">
                          <RoleBadge role={u.role} />
                          {u.teamRole && u.teamRole !== u.role && (
                            <span className="text-muted-foreground text-xs">
                              Team:{' '}
                              <span className="bg-muted rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                                {u.teamRole}
                              </span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {isSuspended ? (
                          <StatusBadge
                            status="SUSPENDED"
                            icon={ShieldOff}
                            label="Suspended"
                            className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                          />
                        ) : (
                          <StatusBadge
                            status="ACTIVE"
                            icon={Shield}
                            label="Active"
                            className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {u.role === 'SUPER_ADMIN' ? (
                          <span className="text-muted-foreground text-sm italic">All projects</span>
                        ) : u.teamProjectName ? (
                          <span className="bg-muted rounded px-2 py-0.5 text-xs">
                            {u.teamProjectName}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">No team project</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-muted-foreground text-sm">
                          {formatDate(u.createdAt)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {canManageUsers && u.id !== user?.id && (
                          <button
                            className="hover:bg-muted rounded p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              const menuData = {
                                userId: u.id,
                                userName: `${u.firstName} ${u.lastName}`,
                                userRole: u.role,
                                isActive: u.isActive !== false,
                                top: rect.bottom + 4,
                                left: rect.right,
                              };
                              setMenu(menuData);
                              setRoleSubmenuOpen(false);
                            }}
                          >
                            <MoreVertical className="text-muted-foreground h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Action dropdown — rendered OUTSIDE the overflow-hidden table card */}
      {/* Uses fixed positioning so it floats above everything              */}
      {/* ----------------------------------------------------------------- */}
      {menu && (
        <>
          {/* Invisible backdrop to catch outside clicks */}
          <div className="fixed inset-0 z-[9998]" onClick={closeMenu} />

          {/* The actual dropdown */}
          <div
            className="bg-card fixed z-[9999] w-52 rounded-lg border shadow-lg"
            style={{ top: menu.top, left: menu.left - 208 }}
          >
            <div className="py-1">
              {/* Change Role */}
              <button
                className="hover:bg-muted flex w-full items-center gap-2 px-4 py-2 text-left text-sm"
                onClick={() => setRoleSubmenuOpen(!roleSubmenuOpen)}
              >
                <UserCog className="h-4 w-4 text-blue-500" />
                Change Role
                <span className="text-muted-foreground ml-auto text-xs">
                  {menu.userRole?.replace('_', ' ')}
                </span>
              </button>

              {roleSubmenuOpen && (
                <div className="border-t px-2 py-1">
                  {(['SUPER_ADMIN', 'PROJECT_ADMIN', 'MODERATOR'] as UserRole[]).map((r) => (
                    <button
                      key={r}
                      disabled={menu.userRole === r}
                      onClick={(e) => {
                        // stopPropagation defends against an ancestor click
                        // handler racing the same event after the dropdown
                        // unmounts (closeMenu) — observed symptom was the
                        // ConfirmDialog mounting and instantly dismissing on
                        // the bubbled click hitting its own backdrop.
                        e.stopPropagation();
                        // Open the confirmation first so it's in the tree
                        // before the dropdown unmounts. Order matters: a
                        // single-tick gap between unmount and mount caused
                        // the dialog to never appear in some browsers.
                        const payload = {
                          type: 'role' as const,
                          userId: menu.userId,
                          userName: menu.userName,
                          newRole: r,
                        };
                        setConfirmAction(payload);
                        closeMenu();
                      }}
                      className={`flex w-full items-center rounded px-3 py-1.5 text-left text-xs ${
                        menu.userRole === r
                          ? 'text-muted-foreground cursor-default font-medium'
                          : 'hover:bg-muted'
                      }`}
                    >
                      {menu.userRole === r && (
                        <span className="mr-1.5 text-green-500">&#10003;</span>
                      )}
                      {r.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              )}

              {/* Ghost Login */}
              <button
                className="hover:bg-muted flex w-full items-center gap-2 px-4 py-2 text-left text-sm"
                onClick={() => {
                  const id = menu.userId;
                  closeMenu();
                  impersonateMutation.mutate(id);
                }}
              >
                <LogIn className="h-4 w-4 text-purple-500" />
                Ghost Login
              </button>

              {/* Suspend / Unsuspend */}
              <button
                className="hover:bg-muted flex w-full items-center gap-2 px-4 py-2 text-left text-sm"
                onClick={() => {
                  const action = menu.isActive ? 'suspend' : 'unsuspend';
                  closeMenu();
                  setConfirmAction({ type: action, userId: menu.userId, userName: menu.userName });
                }}
              >
                {menu.isActive ? (
                  <>
                    <ShieldOff className="h-4 w-4 text-amber-500" />
                    Suspend User
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 text-green-500" />
                    Unsuspend User
                  </>
                )}
              </button>

              <div className="my-1 border-t" />

              {/* Delete */}
              <button
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                onClick={() => {
                  closeMenu();
                  setConfirmAction({
                    type: 'delete',
                    userId: menu.userId,
                    userName: menu.userName,
                  });
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete User
              </button>
            </div>
          </div>
        </>
      )}

      {/* Deleting a user cascades into the teams they own, so it gets a
          dedicated dialog that shows the blast radius and collects ownership
          transfers — the generic confirm can't express that. */}
      {confirmAction?.type === 'delete' && (
        <DeleteUserDialog
          userId={confirmAction.userId}
          userName={confirmAction.userName}
          isPending={deleteMutation.isPending}
          onConfirm={(transferOwnership) =>
            deleteMutation.mutate({
              userId: confirmAction.userId,
              transferOwnership: Object.keys(transferOwnership).length
                ? transferOwnership
                : undefined,
            })
          }
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* Confirm dialog */}
      {confirmAction &&
        confirmAction.type !== 'delete' &&
        (() => {
          const config: Record<
            string,
            { title: string; message: string; confirmLabel: string; danger: boolean }
          > = {
            suspend: {
              title: 'Suspend User',
              message:
                `Suspend ${confirmAction.userName}? They lose access, and every project on ` +
                'the teams they own stops serving — their subscription is paused so they ' +
                'are not charged while it is down. Reversible.',
              confirmLabel: 'Suspend',
              danger: true,
            },
            unsuspend: {
              title: 'Unsuspend User',
              message:
                `Restore access for ${confirmAction.userName}? Projects suspended by this ` +
                'account’s suspension come back and billing resumes. Projects suspended ' +
                'separately stay suspended.',
              confirmLabel: 'Unsuspend',
              danger: false,
            },
            role: {
              title: 'Change Role',
              message: `Change ${confirmAction.userName}'s role to ${confirmAction.newRole?.replace('_', ' ')}?`,
              confirmLabel: 'Change Role',
              danger: false,
            },
          };
          const c = config[confirmAction.type] || config.suspend;
          return (
            <ConfirmDialog
              open
              title={c.title}
              message={c.message}
              confirmLabel={c.confirmLabel}
              danger={c.danger}
              isPending={updateMutation.isPending}
              onConfirm={() => {
                if (confirmAction.type === 'suspend') {
                  updateMutation.mutate({
                    userId: confirmAction.userId,
                    data: { isActive: false },
                  });
                } else if (confirmAction.type === 'unsuspend') {
                  updateMutation.mutate({ userId: confirmAction.userId, data: { isActive: true } });
                } else if (confirmAction.type === 'role') {
                  updateMutation.mutate({
                    userId: confirmAction.userId,
                    data: { role: confirmAction.newRole },
                  });
                }
              }}
              onCancel={() => setConfirmAction(null)}
            />
          );
        })()}

      {showCreateModal && <CreateUserModal onClose={() => setShowCreateModal(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create User Modal
// ---------------------------------------------------------------------------
function CreateUserModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>('PROJECT_ADMIN');
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: CreateUserData) => apiHelpers.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User created — they will receive an email to set their password');
      onClose();
    },
    onError: (err) => {
      setError(getErrorMessage(err, 'Failed to create user'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({ email, firstName, lastName, role });
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-card w-full max-w-md rounded-xl border shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b p-6">
            <h2 className="text-lg font-semibold">Add New User</h2>
            <button onClick={onClose} className="hover:bg-muted rounded p-1">
              <X className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4 p-6">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20">
                {error}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">First Name *</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Last Name *</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Role *</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as any)}
                className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
              >
                <option value="PROJECT_ADMIN">Project Admin</option>
                <option value="MODERATOR">Moderator</option>
              </select>
            </div>
            <p className="text-muted-foreground text-xs">
              A team and default project will be created automatically. The user will receive an
              email to set their password.
            </p>
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="hover:bg-muted flex-1 rounded-lg border px-4 py-2 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="bg-primary text-primary-foreground hover:bg-primary/90 flex flex-1 items-center justify-center rounded-lg px-4 py-2 font-medium disabled:opacity-50"
              >
                {createMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Create User'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
